
import { useMemo, useState } from "react";
import Select, { components as selectComponents } from "react-select";

/** Sentinel `optionValue` for the injected select-all row. It has to be SOME
 *  value because react-select keys every option by `getOptionValue`, and a
 *  duplicate/undefined key there breaks focus tracking. Namespaced so it cannot
 *  collide with a real id. */
const SELECT_ALL_VALUE = "__filterselect_select_all__";

/** Tallest the option list may get. A ceiling, never a floor: react-select's
 *  own measurement wins whenever there is less room than this — see
 *  `customStyles.menuList`. */
const MAX_MENU_HEIGHT = 300;

// Multi-selects stop accepting picks at this many. Every id list ends up in a
// GET query string that the API caps and the request line bounds, and a
// select-all over a few hundred employees blew past both. Pass
// `maxSelections={0}` to lift the cap for a specific control.
const DEFAULT_MAX_SELECTIONS = 50;

/** Does this option survive the current search text?
 *
 *  Extracted to module scope because it now has TWO callers that must never
 *  disagree: `filterOption`, which decides what react-select renders, and the
 *  select-all row's count, which must name exactly that same set ("Select all
 *  (12)" has to mean the twelve rows underneath it). Duplicating the predicate
 *  is how those two drift apart — a trailing space handled in one and not the
 *  other is enough to make the count lie.
 *
 *  Behaviour is byte-for-byte what `filterOption` did inline before: empty
 *  needle matches everything, then the label, then any other string/number
 *  field on the option (`emp_no`, `email`, …). */
const optionMatches = (data, needle, optionLabel, optionValue) => {
  if (!needle) return true;
  const label = data?.[optionLabel];
  if (typeof label === "string" && label.toLowerCase().includes(needle)) return true;
  // Also match any other string/number field on the option (e.g. emp_no, email)
  for (const key in data) {
    if (key === optionLabel || key === optionValue) continue;
    const v = data[key];
    if (v == null) continue;
    if (typeof v === "string" || typeof v === "number") {
      if (String(v).toLowerCase().includes(needle)) return true;
    }
  }
  return false;
};

/** Option row used only when `checkboxMenu` is on.
 *
 *  It wraps react-select's OWN Option rather than rendering a bare div, so every
 *  behaviour that arrives through `innerProps` survives untouched: the generated
 *  option id, the click/hover handlers, `role="option"` and `aria-selected`. Only
 *  the children are replaced.
 *
 *  The checkbox is deliberately inert — `aria-hidden`, `tabIndex={-1}`,
 *  `readOnly`, `pointer-events: none`. ARIA forbids focusable descendants inside
 *  `role="option"`, and the option itself already publishes `aria-selected`, so a
 *  real checkbox here would announce the same state twice and break arrow-key
 *  navigation. It is a visual affordance only. (react-select suppresses
 *  `aria-selected` on Apple devices to dodge a VoiceOver bug and announces
 *  selection through its live region instead; that path is unchanged by this.) */
/*  It also renders the opt-in select-all row, which is a real option injected at
 *  the head of the list rather than a control of its own (see `selectAll`
 *  below). Branching INSIDE this one component matters: the alternative — a
 *  second component type chosen per row — would be a type that varies with
 *  data, and this file has already been bitten twice by a react-select
 *  component type changing underneath a live interaction. Row 0 is always the
 *  select-all row and every other row never is, so React reconciles each
 *  position against a stable type.
 *
 *  The select-all row's checkbox is tri-state: `indeterminate` is a DOM
 *  property with no HTML attribute, so it can only be set through a ref. Its
 *  checked/indeterminate state is read from `selectProps.selectAllState`, never
 *  from `props.isSelected` — the sentinel is never IN the value, so
 *  `isSelected` is permanently false for it. */
const CheckboxOption = (props) => {
  const isSelectAll = props.data?.__selectAll === true;
  const selectAllState = props.selectProps?.selectAllState;
  const checked = isSelectAll ? !!selectAllState?.allSelected : props.isSelected;
  // Some-but-not-all ticked. Only ever true on the select-all row.
  const indeterminate = isSelectAll && !!selectAllState?.someSelected && !selectAllState?.allSelected;
  return (
    <selectComponents.Option
      {...props}
      // The row is a `role="option"`, and react-select gives it
      // `aria-selected="false"` — correct in its own terms (the sentinel is
      // never in the value) but wrong as a description of the tick the user
      // sees. ARIA 1.2 allows `aria-checked` on `role="option"` precisely for
      // multi-select lists, and unlike `aria-selected` it has a "mixed" state,
      // so the tri-state is conveyed structurally rather than only in prose.
      // The `aria-label` stays: it is what carries the COUNT, which no state
      // attribute can express.
      innerProps={
        isSelectAll && selectAllState
          ? {
            ...props.innerProps,
            "aria-label": selectAllState.ariaLabel,
            "aria-checked": selectAllState.allSelected
              ? "true"
              : selectAllState.someSelected ? "mixed" : "false",
          }
          : props.innerProps
      }
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={checked}
          ref={(el) => { if (el) el.indeterminate = indeterminate; }}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          style={{ pointerEvents: "none", flexShrink: 0, margin: 0, accentColor: "#D3001A", width: 15, height: 15 }}
        />
        <span style={isSelectAll ? { fontWeight: 600 } : undefined}>{props.children}</span>
      </span>
    </selectComponents.Option>
  );
};

/** Caps how many selected chips are rendered in the control.
 *
 *  Without this, a "select all" over a few hundred options renders a few hundred
 *  chips — each a flex row with its own remove button — and the whole control
 *  re-renders on every keystroke in the search box. Past the cap we render one
 *  "+n more" summary and drop the rest; the selection itself is untouched, only
 *  its rendering is summarised. */
const makeCappedMultiValue = (limit) => {
  const CappedMultiValue = (props) => {
    if (props.index < limit) return <selectComponents.MultiValue {...props} />;
    if (props.index > limit) return null;
    const hidden = (props.selectProps.value?.length || 0) - limit;
    if (hidden <= 0) return null;
    return (
      <span
        style={{
          flexShrink: 0,
          alignSelf: "center",
          padding: "0 6px",
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          whiteSpace: "nowrap",
        }}
      >
        +{hidden} more
      </span>
    );
  };
  return CappedMultiValue;
};

/** Adds the caller's `aria-describedby` to react-select's inner input.
 *
 *  `aria-describedby` is NOT one of react-select 5's passthrough aria props.
 *  It accepts `aria-label`, `aria-labelledby`, `aria-invalid`, `aria-live` and
 *  `aria-errormessage` at the top level and forwards those to the combobox
 *  input, but it OWNS `aria-describedby`: it always points it at its own
 *  placeholder element, or at its live region on first focus, and that
 *  assignment comes last, so a top-level `aria-describedby` is silently
 *  dropped. Overriding the Input component is the only place the attribute can
 *  actually be set.
 *
 *  The caller's id is appended to whatever react-select computed rather than
 *  replacing it, because that internal id is how the current value and the
 *  placeholder get announced. `aria-describedby` takes a list, and this is
 *  exactly what it is for.
 *
 *  ONE component, defined once at module scope — never a factory. A factory
 *  minted a fresh component TYPE whenever the described-by value changed, and
 *  React unmounts and remounts on a type change: selecting or clearing a size
 *  destroyed react-select's <input> mid-interaction and dropped keyboard focus
 *  to <body> (react-select re-focuses only on isDisabled/menuIsOpen
 *  transitions, so nothing put it back). So the value is NOT closed over — it
 *  is read from `selectProps`, the full props object react-select hands to
 *  every component override, letting the value change freely while the type
 *  stays put. That is why the caller's `aria-describedby` is still passed to
 *  <Select> even though it never reaches the DOM from there. */
//  Rendered for EVERY select, including the ~113 callers that pass neither
//  aria value — see componentOverrides. For those it is a pure pass-through:
//  `selectProps["aria-describedby"]` is undefined, so the merge collapses to
//  react-select's own id, exactly the attribute the default Input would have
//  rendered. When BOTH are absent the join yields "" and the `|| undefined`
//  turns it into an omitted attribute rather than `aria-describedby=""`.
const DescribedInput = (props) => (
  <selectComponents.Input
    {...props}
    aria-describedby={
      [props["aria-describedby"], props.selectProps?.["aria-describedby"]]
        .filter(Boolean).join(" ") || undefined
    }
  />
);

// `inputId` is optional and additive: react-select generates its own id when it
// is absent, so the 40-odd existing callers are unaffected. Passing one lets a
// caller point a <label htmlFor> at the combobox, which is the only way to give
// these selects an accessible name.
//
// `checkboxMenu`, `maxVisibleValues`, `aria-describedby` and `aria-invalid` are
// opt-in in the same spirit. All four default to off/undefined, and when unset
// none of them is passed to the underlying <Select> (see `extraProps` below —
// the props are omitted, not passed as false/undefined).
//
// ONE exception, and it is deliberate: `components={{ Input }}` IS always
// passed, because the Input override must not appear and disappear with a
// value — that swap remounts the input and drops focus (see
// `componentOverrides`). So a caller passing no flag is no longer BYTE-identical
// to before; it is BEHAVIOURALLY identical, since DescribedInput spreads every
// prop through and appends nothing when the aria values are absent.
//
// The two aria props exist because a select can be in an error state that has
// to be announced. Without them the only lever a caller had was the accessible
// NAME (via `inputId` + a <label>), so screens were stuffing error text into
// the label — which makes the name change as the user types and is not what a
// name is for. The description is the right place for it.
// `searchable={false}` swaps react-select's text input for its DummyInput
// (`inputMode="none"`, read-only), so tapping the control on a phone opens
// the menu WITHOUT raising the keyboard. Use it for short fixed lists in
// modals: a keyboard inside a position:fixed dialog makes iOS Safari scroll
// the document to reveal the input, and on some devices that scroll is never
// undone — the fixed bottom nav then sits stranded until Safari is relaunched
// (the 2026-09-13 "bottom nav not sticky" report). Default stays true.
// `menuPosition="fixed"` positions the (portaled) menu against the viewport,
// which is what a control inside a scrolling drawer needs: with "absolute"
// the menu is measured against the document and can end up squeezed to a few
// rows when the control sits just above the drawer footer.
//
// `selectAll` (opt-in, and only meaningful alongside `isMulti checkboxMenu`)
// adds a sticky first row to the menu that ticks or clears everything CURRENTLY
// VISIBLE — the options surviving whatever the user has typed in the search
// box, not the whole option list. That distinction is the entire point: "select
// all" on a 400-option picker is rarely what anyone wants, whereas "type
// 'kitchen', select all 9" is. Selections made outside the current search are
// left alone in both directions, so narrowing and ticking twice unions rather
// than replaces.
//
// It is implemented as a real option injected at the head of `options`, NOT as
// a button beside the control or a custom sticky node in the MenuList, for two
// reasons that are both about focus:
//   - Keyboard. As an option it is reached with ArrowDown and toggled with
//     Enter, the same keys as every other row, and focus never leaves the
//     combobox input. A focusable control inside the portaled menu would be a
//     tab stop out of document order, and focusing it blurs the input — which
//     closes the menu and unmounts the very control being focused, dropping
//     focus to <body>.
//   - Visibility. A button outside the menu cannot see the search text typed
//     inside it, so it can only ever mean "all N", never "these 9".
// `position: sticky` on that row (see `customStyles.option`) keeps it pinned
// while the list scrolls, so it reads as a header without being one.
// `maxMenuHeight` is forwarded rather than left to react-select's default so
// that the ceiling in `customStyles.menuList` has something to be a ceiling
// OVER: it is the number MenuPlacer starts from before subtracting whatever
// room the viewport actually leaves. Defaulted to react-select's own 300, so
// no existing caller changes behaviour by not passing it.
const FilterSelect = ({options, value, isMulti=false, placeholder, isLoading, optionLabel, optionValue, onChange, isClearable, className, isDisabled=false, defaultValue, menuPlacement="auto", menuPosition="absolute", minMenuHeight=140, maxMenuHeight=MAX_MENU_HEIGHT, noOptionsLabel="employees", inputId, checkboxMenu=false, selectAll=false, maxVisibleValues=0, maxSelections=DEFAULT_MAX_SELECTIONS, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, searchable=true}) => {

  /** Select-all is meaningless without a multi-value tick menu to put it in, so
   *  it is gated on both rather than trusted to the caller. Every select-all
   *  code path below is dead when this is false, and NO extra prop reaches
   *  <Select> — which is what keeps the ~113 callers that pass none of this
   *  rendering exactly the <Select> they rendered before. */
  const selectAllEnabled = selectAll && checkboxMenu && isMulti;

  /** react-select owns the search text, and normally keeps it to itself. The
   *  select-all row has to name how many options that text leaves visible, and
   *  the toggle has to act on exactly those — so for this one mode the input is
   *  a controlled value. It is controlled by MIRRORING, never by deciding:
   *  whatever react-select proposes is accepted verbatim, including the ''
   *  it sends on menu-close, blur and set-value. So the observable behaviour is
   *  identical to the uncontrolled default; the value is simply also readable
   *  here.
   *
   *  Reading it from a ref instead would be wrong, and subtly so: react-select's
   *  `setValue` fires `onInputChange('', {action:'set-value'})` BEFORE it fires
   *  `onChange`, so a ref would already have been cleared to '' by the time the
   *  toggle ran, and "select all 9 matching 'kitchen'" would select all 400.
   *  State read during render is captured in the closure the click was
   *  dispatched against, which is the value the user was actually looking at. */
  const [inputValue, setInputValue] = useState("");

  /** The sentinel option object. Memoised so its IDENTITY is stable across
   *  keystrokes: react-select stores the focused option by reference, so
   *  rebuilding this per render would drop the focus ring off the select-all
   *  row the moment the user typed. Its LABEL still has to carry a live count,
   *  which is why the count is applied in `getOptionLabel` (a function of the
   *  option) rather than baked into the object. */
  const selectAllOption = useMemo(
    () => ({ __selectAll: true, [optionValue]: SELECT_ALL_VALUE, [optionLabel]: "Select all" }),
    [optionLabel, optionValue]
  );

  /** The options the user can currently see — `options` narrowed by the same
   *  predicate `filterOption` applies. This is what "all" means. */
  const visibleOptions = useMemo(() => {
    if (!selectAllEnabled) return [];
    const needle = inputValue.toLowerCase();
    return (options || []).filter((o) => optionMatches(o, needle, optionLabel, optionValue));
  }, [selectAllEnabled, options, inputValue, optionLabel, optionValue]);

  const selectedValues = useMemo(
    () => new Set((Array.isArray(value) ? value : []).map((v) => v?.[optionValue])),
    [value, optionValue]
  );

  /** Counted over the VISIBLE options only, so ticking every match of a search
   *  shows a full tick even while hundreds of unsearched options sit unticked —
   *  which is correct, because that is the set this row acts on. */
  const selectedVisibleCount = useMemo(
    () => visibleOptions.reduce((n, o) => (selectedValues.has(o[optionValue]) ? n + 1 : n), 0),
    [visibleOptions, selectedValues, optionValue]
  );
  const allVisibleSelected = visibleOptions.length > 0 && selectedVisibleCount === visibleOptions.length;

  /** Selection cap. At the cap every unselected option greys out and a note
   *  under the field says so; select-all keeps its plain label and simply
   *  fills up to the cap (see handleChange). Deselecting always works. */
  const capEnabled = isMulti && maxSelections > 0;
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const atCap = capEnabled && selectedCount >= maxSelections;
  const isOptionDisabled = (option) => {
    if (!capEnabled || option?.__selectAll) return false;
    return atCap && !selectedValues.has(option?.[optionValue]);
  };

  /** Handed to <Select> as a plain prop so it arrives in `selectProps`, which is
   *  how the module-scope CheckboxOption reads it without the component type
   *  ever closing over a value. */
  const selectAllState = selectAllEnabled ? {
    allSelected: allVisibleSelected,
    someSelected: selectedVisibleCount > 0,
    ariaLabel: allVisibleSelected
      ? `Clear all ${visibleOptions.length} shown options`
      : `Select all ${visibleOptions.length} shown options`
        + (selectedVisibleCount > 0 ? `, ${selectedVisibleCount} already selected` : ""),
  } : undefined;

  /** Intercepts the sentinel and expands it into a real multi-value change;
   *  every other change is forwarded to the caller untouched.
   *
   *  Both directions are a UNION/DIFFERENCE against the visible set rather than
   *  a wholesale replace, so a selection the user made under a different search
   *  is never silently discarded by a select-all they meant to apply to what is
   *  in front of them. */
  const handleChange = (newValue, actionMeta) => {
    // 'select-option' reports the clicked option as `option`; the deselect path
    // reports it as `removedValue`. The sentinel is never in `value`, so only
    // the former can fire for it — the latter is read defensively.
    const touched = actionMeta?.option || actionMeta?.removedValue;
    if (!touched?.__selectAll) {
      onChange(newValue, actionMeta);
      return;
    }
    const visibleKeys = new Set(visibleOptions.map((o) => o[optionValue]));
    const current = Array.isArray(value) ? value : [];
    const toAdd = visibleOptions.filter((o) => !selectedValues.has(o[optionValue]));
    const room = capEnabled ? Math.max(0, maxSelections - current.length) : toAdd.length;
    const next = allVisibleSelected
      ? current.filter((v) => !visibleKeys.has(v?.[optionValue]))
      : [...current, ...toAdd.slice(0, room)];
    onChange(next, {
      action: allVisibleSelected ? "deselect-all" : "select-all",
      name: actionMeta?.name,
    });
  };

  const customStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }), /* Ensures visibility */
    menuList: (base) => ({
      ...base,
      /** Never taller than 300px, and never taller than the room react-select
       *  measured either — the flat `maxHeight: "300px"` this replaced threw
       *  the measurement away.
       *
       *  `base.maxHeight` is `MenuPlacer`'s CONSTRAINED height, not the
       *  `maxMenuHeight` it started from: under `menuPlacement="auto"` it is
       *  reduced to the space actually available once the control is close to
       *  a viewport edge. So this is not merely a ceiling that never binds —
       *  whenever there is less than 300px of room, `base.maxHeight` is the
       *  smaller number and it is the one that wins, which is what keeps the
       *  menu inside the viewport and the scrolling inside the list.
       *
       *  Stated plainly because an earlier commit claimed otherwise: this is
       *  NOT what made the select-all row appear unpinned. See `paddingTop`
       *  directly below for that. */
      maxHeight: Math.min(base.maxHeight ?? MAX_MENU_HEIGHT, MAX_MENU_HEIGHT),

      /** THE sticky bug, measured in a browser rather than reasoned about.
       *
       *  react-select's own `menuListCSS` sets `padding-top: baseUnit` (4px),
       *  and a sticky element's `top` offset resolves against its scrollport's
       *  PADDING box — not its border box. So `top: 0` on the select-all row
       *  pinned it 4px BELOW the menu's visible top edge, and those 4px stayed
       *  a live window onto the scrolling content behind it.
       *
       *  Measured with the menu open and the list scrolled: menuList top
       *  187.3, pinned row top 191.3, and `document.elementFromPoint` in the
       *  band between them returned the option "Al Wahda Mall" — a real row,
       *  painted above "Select all", clipped to a 4px sliver. Which is exactly
       *  the "an option is visible above Select all, cut off at its top" the
       *  bug report described, sitting in what looks like the gap under the
       *  control (that gap is this 4px plus the menu's own 8px gutter).
       *
       *  Zeroed only when a select-all row actually exists, so the ~113 plain
       *  selects keep react-select's spacing untouched. For the menus that do
       *  have one the row is meant to read as a pinned header, and a header
       *  belongs flush against the top edge with nothing able to slide above
       *  it. `paddingBottom` is deliberately left alone — nothing is sticky
       *  down there. */
      ...(selectAllEnabled ? { paddingTop: 0 } : {}),

      overflowY: "auto", // Enable vertical scrolling
      overflowX: "hidden"
    }),
    option: (provided, state) => {
      const base = {

        ...provided,
        backgroundColor: state.isFocused ? "#D3001A" : "#fff",
        color: state.isFocused ? "#fff" : "#000",
        "&:hover": {
          backgroundColor: "#D3001A",
        },
      };
      if (!state.data?.__selectAll) return base;
      // Pinned to the top of the scrolling menuList so it stays reachable in a
      // long list. It needs an opaque background (inherited from `base`) or the
      // options scrolling underneath show through.
      return {
        ...base,
        position: "sticky",
        top: 0,
        zIndex: 2,
        borderBottom: "1px solid #E5E7EB",
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      display: "flex",
      flexWrap: "nowrap",      // single row, no wrapping
      overflowX: "auto",        // horizontal scroll when many chips
      overflowY: "hidden",
      whiteSpace: "nowrap",
      maxWidth: "100%",
      gap: isMulti ? 4 : 0,
      // hide horizontal scrollbar visually but keep scroll capability
      scrollbarWidth: "thin",
    }),
    multiValue: (provided) => ({
      ...provided,
      flexShrink: 0,            // chips keep their natural width
      borderRadius: 6,
      background: "rgba(211,0,26,0.08)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#1F1F1F",
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "visible",      // show full name within the chip
      textOverflow: "clip",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6B7280",
      "&:hover": { background: "rgba(211,0,26,0.16)", color: "#D3001A" },
    }),
    control: (base) => ({
      ...base,
      minHeight: 40,
      fontSize: 12,
    }),
  };


  // const customStyles = {
  //   control: (provided) => ({
  //     ...provided,
  //     display: "flex",
  //     overflow: "hidden"
  //   }),
  //   valueContainer: (provided) => ({
  //     ...provided,
  //     display: "flex",
  //     flexWrap: "nowrap", // Prevent wrapping
  //     overflowX: "auto", // Enable horizontal scrolling
  //     whiteSpace: "nowrap",
  //     maxWidth: "100%"
  //   }),
  //   multiValue: (provided) => ({
  //     ...provided,
  //     marginRight: "4px" // Space between selected items
  //   })
  // };

  /** The component overrides the opt-in flags add, keyed only on WHICH
   *  overrides apply — never on their values.
   *
   *  Separate from `extraProps` below on purpose: react-select rebuilds its
   *  internal component map whenever the identity of `components` changes, so
   *  this must not be rebuilt merely because a described-by id or an invalid
   *  flag changed. Every component in it is a stable module-scope type, so the
   *  map's contents are referentially fixed for a given set of flags, and both
   *  the object's identity and every type inside it survive any number of
   *  re-renders driven by the aria values. */
  const componentOverrides = useMemo(() => {
    // Input is overridden ALWAYS, for every caller, and this must never become
    // conditional again. react-select's renderInput does
    // `const Input = this.getComponents().Input` and then
    // `createElement(Input, …)`, so whether an override EXISTS decides the
    // element type. Gating it on `aria-describedby` being truthy meant that
    // picking a size — flipping the id from undefined to a string — swapped the
    // type between react-select's default Input and this one, and React
    // unmounts across a type change: the <input> was destroyed mid-interaction
    // and focus fell to <body>. Making the factory stable fixed only half of
    // that; the presence flip was the other half.
    //
    // Gating on prop PRESENCE rather than value would also work today and
    // would leave the other callers untouched, but it is a trap: a caller that
    // conditionally spreads `{...(err && {'aria-describedby': id})}` silently
    // reintroduces the remount. A type that is decided once at module scope
    // cannot be got wrong from a call site.
    const overrides = { Input: DescribedInput };
    if (checkboxMenu) overrides.Option = CheckboxOption;
    if (isMulti && maxVisibleValues > 0) overrides.MultiValue = makeCappedMultiValue(maxVisibleValues);
    return overrides;
  }, [checkboxMenu, isMulti, maxVisibleValues]);

  /** Everything else the opt-in flags add.
   *
   *  `components` is now always present, so a caller passing no opt-in flag no
   *  longer renders a byte-identical <Select> — it renders one carrying a
   *  stable `components={{ Input }}`. That is a deliberate, stated trade: for
   *  those callers DescribedInput is a pure pass-through (it spreads every
   *  prop and appends nothing, since both aria values are undefined), so the
   *  DOM and behaviour are the same, but the claim is now "behaviourally
   *  identical", not "byte-identical". Correctness that no call site can
   *  undo is worth that. */
  const extraProps = useMemo(() => {
    const extra = { components: componentOverrides };

    if (checkboxMenu) {
      // Ticking must not dismiss the menu, and a ticked option has to stay in
      // the list to be untickable — which is exactly what hiding selected
      // options would prevent.
      extra.closeMenuOnSelect = false;
      extra.hideSelectedOptions = false;
      extra.blurInputOnSelect = false;
    }
    // `aria-invalid` IS a react-select passthrough and reaches the DOM input on
    // its own. `aria-describedby` is passed here too even though react-select
    // overwrites it on the input — it still arrives in `selectProps`, which is
    // where DescribedInput above reads it from.
    if (ariaInvalid !== undefined) extra["aria-invalid"] = ariaInvalid;
    if (ariaDescribedBy) extra["aria-describedby"] = ariaDescribedBy;
    return extra;
  }, [checkboxMenu, ariaInvalid, ariaDescribedBy, componentOverrides]);

  /** Deliberately OUTSIDE the memo above and spread separately: `selectAllState`
   *  is rebuilt every render (it tracks the value and the search text), and
   *  folding it into `extraProps` would churn the object that also carries
   *  `components` — the one prop whose identity react-select uses to rebuild its
   *  internal component map. Null when the mode is off, so the spread adds
   *  nothing at all for every other caller. */
  const selectAllProps = selectAllEnabled ? {
    inputValue,
    onInputChange: (next) => setInputValue(next ?? ""),
    selectAllState,
  } : null;

  /** Only when a select-all row can actually exist, and only while it has
   *  something to act on: with no visible options the row would read "Select
   *  all (0)", and — worse — its mere presence would suppress the "No X found
   *  matching …" message, which is the one thing the user needs at that moment. */
  const optionsWithSelectAll = useMemo(
    () => (selectAllEnabled && visibleOptions.length > 0 ? [selectAllOption, ...(options || [])] : options),
    [selectAllEnabled, visibleOptions.length, selectAllOption, options]
  );

  return (

          <div className=" form-focus select-focus">
            <Select
            inputId={inputId}
            {...extraProps}
            {...(selectAllProps || {})}
            defaultValue={defaultValue}
            isDisabled={isDisabled || isLoading}
            isClearable={isClearable}
            isSearchable={searchable}
            onChange={selectAllEnabled ? handleChange : onChange}
                getOptionLabel={option => (
                  // The count lives here, not on the option object, so the
                  // object's identity can stay stable while its label tracks
                  // the search. `__selectAll` is undefined on every real
                  // option, so this is a pass-through for every other caller.
                  option?.__selectAll
                    ? `Select all (${visibleOptions.length})`
                    : option[optionLabel]
                )}
                getOptionValue={option => option[optionValue]}
              isOptionDisabled={isOptionDisabled}
              isLoading={isLoading}
              menuPlacement={menuPlacement}
              menuPosition={menuPosition}
              minMenuHeight={minMenuHeight}
              maxMenuHeight={maxMenuHeight}
              options={optionsWithSelectAll}
              placeholder={isLoading ? "Loading…" : placeholder}
              styles={customStyles}
              value={value}
              isMulti={isMulti}
              className={className}
              menuPortalTarget={document.body} /* Fix dropdown being hidden */
              filterOption={(option, search) => (
                // The select-all row is never filtered out — it describes the
                // filtered set, so hiding it when the search excludes its own
                // label ("Select all") would remove it exactly when it is most
                // useful. Everything else goes through the shared predicate
                // that `visibleOptions` also uses, so the two can never
                // disagree about what "all" means.
                option.data?.__selectAll
                  ? true
                  : optionMatches(option.data, search.toLowerCase(), optionLabel, optionValue)
              )}
              noOptionsMessage={({ inputValue }) => 
                inputValue ? `No ${noOptionsLabel} found matching "${inputValue}"` : `No ${noOptionsLabel} available`
              }
            />
            {atCap && (
              <small className="d-block mt-1" style={{ fontSize: 11.5, color: "#6b7280" }}>
                Maximum {maxSelections} at a time
              </small>
            )}
            {/* <label className="focus-label">Designation</label> */}
          </div>
       
  );
};

export default FilterSelect;
